import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../hooks/useApi";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminImports() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: log } = useQuery({ queryKey: ["import-log"], queryFn: async () => (await api.get("/admin/import/log")).data });
  const { data: manifests } = useQuery({ queryKey: ["import-manifests"], queryFn: async () => (await api.get("/admin/import/manifests")).data });

  const runImport = async () => {
    const res = await api.post("/admin/import/rss");
    toast.success(`RSS import complete — ${res.data.imported} new, ${res.data.skipped} already present.`);
    qc.invalidateQueries({ queryKey: ["import-log"] });
  };

  const runGoldImport = async () => {
    await api.post("/admin/import/gold");
    toast.info("Gold Vault import run — see manifest below for source/staged/blocked counts.");
    qc.invalidateQueries({ queryKey: ["import-manifests"] });
  };

  const runTmsFullImport = async () => {
    await api.post("/admin/import/tms-full");
    toast.info("Muscle School full-content import run — see manifest below.");
    qc.invalidateQueries({ queryKey: ["import-manifests"] });
  };

  return (
    <div data-testid="admin-imports-page">
      <h1 className="font-display text-display-xs mb-6">Imports &amp; Rights Registry</h1>

      <div className="bg-surface border border-border p-6 mb-8 max-w-xl">
        <p className="text-sm mb-1">Podcast RSS Import</p>
        <p className="text-xs text-white/70 mb-4">Source: anchor.fm/s/de3e491c/podcast/rss. Duplicate detection via GUID.</p>
        <button data-testid="admin-run-rss-import" onClick={runImport} className="bg-white text-black px-5 py-2.5 text-sm uppercase tracking-wide">Run Import</button>
      </div>

      <div className="bg-surface border border-border p-6 mb-8 max-w-xl">
        <p className="text-sm mb-1">Gold Vault Import Adapter</p>
        <p className="text-xs text-white/70 mb-4">Ready to receive gold_content_records.json, gold_media_manifest.json, gold_entitlement_definitions.json, gold_source_manifest.json. No authoritative export has been supplied yet — running now will report BLOCKED per file.</p>
        <button data-testid="admin-run-gold-import" onClick={runGoldImport} className="bg-white text-black px-5 py-2.5 text-sm uppercase tracking-wide">Run Gold Import</button>
      </div>

      <div className="bg-surface border border-border p-6 mb-8 max-w-xl">
        <p className="text-sm mb-1">Muscle School Full-Content Import Adapter</p>
        <p className="text-xs text-white/70 mb-4">The 7/35/106 outline is already staged. This adapter is ready to receive the full authoritative lesson bodies (tms_curriculum.json, tms_lesson_content.json, tms_claims.json, tms_sources.json, tms_source_manifest.json) once exported.</p>
        <button data-testid="admin-run-tms-full-import" onClick={runTmsFullImport} className="bg-white text-black px-5 py-2.5 text-sm uppercase tracking-wide">Run TMS Full Import</button>
      </div>

      {manifests?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm uppercase tracking-wide text-white/70 mb-3">Source → Staged Manifests</h2>
          {manifests.map((m) => (
            <div key={m.name} data-testid={`import-manifest-${m.name}`} className="bg-surface border border-border p-4 mb-3 text-xs">
              <p className="text-sm mb-2">{m.name} — run {m.run_at}</p>
              {Object.entries(m.files || {}).map(([file, f]) => (
                <div key={file} className="flex justify-between py-1 border-t border-border">
                  <span className="text-foreground/60">{file}</span>
                  <span className="flex items-center gap-1.5 text-foreground/70">
                    {f.status.startsWith("BLOCKED") ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                    {f.status} — imported {f.records_imported}/{f.source_expected ?? 0}
                  </span>
                </div>
              ))}
              {!m.files && Object.entries(m).filter(([k, v]) => k !== "name" && k !== "run_at" && typeof v === "number").map(([key, value]) => (
                <div key={key} className="flex justify-between py-1 border-t border-border">
                  <span className="text-foreground/60">{key.replace(/_/g, " ")}</span>
                  <span className="text-foreground/70">{value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <h2 className="text-sm uppercase tracking-wide text-white/70 mb-3">Import Log</h2>
      <div className="divide-y divide-white/10 border-t border-b border-white/10">
        {log?.map((l, i) => (
          <div key={i} data-testid={`import-log-row-${i}`} className="py-3 text-sm flex justify-between">
            <span>{l.source} — imported {l.imported}, skipped {l.skipped}</span>
            <span className="text-white/70">{l.run_at}</span>
          </div>
        ))}
        {!log?.length && <p className="text-white/70 text-sm py-4">No import runs yet.</p>}
      </div>
    </div>
  );
}
